<template>
  <div class="relative">
    <div class="bg-white rounded-xl shadow-xl">
      <!-- Loading et Error states -->
      <div v-if="loading" class="text-center py-12">
        <div class="spinner"></div>
        <p class="mt-4 text-gray-600 text-[1.375rem]">Chargement du projet...</p>
      </div>

      <div v-else-if="error" class="bg-red-50 p-6 rounded-xl">
        <p class="text-red-700 text-[1.375rem]">{{ error }}</p>
      </div>

      <div v-else-if="project" class="space-y-6">
        <!-- Bouton de retour -->
        <div class="absolute top-4 left-4 z-10">
          <button 
            @click="router.back()" 
            class="inline-flex items-center px-3 py-2 rounded-lg bg-white/80 backdrop-blur-sm text-gray-700 hover:text-gray-900 hover:bg-white/90 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
            </svg>
            Retour
          </button>
        </div>

        <!-- Layout en deux colonnes -->
        <div class="flex flex-col lg:flex-row">
          <!-- Colonne de gauche - Image -->
          <div class="lg:w-1/2">
            <div class="relative h-96 lg:h-full">
              <img 
                :src="getProjectImage(project.cover)"
                :alt="project.name"
                @error="handleImageError"
                class="w-full h-full object-cover rounded-t-xl lg:rounded-l-xl lg:rounded-tr-none shadow-lg"
              />
              <div class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-6 rounded-b-xl lg:rounded-bl-xl lg:rounded-br-none">
                <h1 class="text-2xl font-bold text-white mb-2">{{ project.name }}</h1>
                <div class="flex items-center mt-2 text-white/90 text-sm">
                  <span>{{ project.country?.name }}</span>
                  <span class="mx-3">•</span>
                  <span>{{ project.partner?.name }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Colonne de droite - Informations -->
          <div class="lg:w-1/2 p-6 space-y-6">
            <!-- Notification -->
            <div v-if="notification" 
              class="transition-all duration-300 transform"
              :class="[
                'p-4 rounded-lg',
                notification.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              ]"
            >
              <p class="text-sm">{{ notification.message }}</p>
            </div>

            <!-- Description du projet -->
            <div class="bg-white rounded-lg shadow-sm p-6">
              <h2 class="text-xl font-semibold mb-4 text-gray-900">Description du projet</h2>
              <div class="text-base text-gray-700 whitespace-pre-line" v-html="sanitizeHtml(project.description)"></div>
            </div>

            <!-- Section Financement -->
            <div v-if="project && project.plans?.length" 
              class="bg-white rounded-lg shadow-sm p-6"
            >
              <div class="flex flex-col gap-4">
                <!-- Message d'erreur en haut de la section -->
                <div v-if="addToCartError" class="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p class="text-sm text-red-600">{{ addToCartError }}</p>
                </div>
                
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div class="space-y-1">
                    <h3 class="text-xl font-semibold text-gray-900">{{ project.plans?.length || 0 }} plan{{ project.plans?.length > 1 ? 's' : '' }} disponible{{ project.plans?.length > 1 ? 's' : '' }}</h3>
                  </div>
                  <div class="flex flex-col sm:items-end gap-2">
                    <div class="flex items-baseline gap-2">
                      <span class="text-2xl font-bold text-gray-900">{{ formatAmount(project.plans[0].amount) }} €</span>
                    </div>
                    <button 
                      @click="project.funding_stats?.funding_percentage >= 100 ? null : addToCart(project.id)"
                      :disabled="project.funding_stats?.funding_percentage >= 100"
                      :class="[
                        'px-6 py-2 text-sm whitespace-nowrap',
                        project.funding_stats?.funding_percentage >= 100 
                          ? 'bg-gray-300 text-gray-700 cursor-not-allowed rounded-lg'
                          : 'btn-primary'
                      ]"
                    >
                      {{ project.funding_stats?.funding_percentage >= 100 
                        ? 'Complet' 
                        : (isGiftMode ? 'Offrir' : 'Financer') }}
                    </button>
                  </div>
                </div>

                <!-- Barre de progression -->
                <div class="mt-6">
                  <ProgressBar
                    :total="project.funding_stats?.total_plans"
                    :remaining="project.funding_stats?.remaining_plans"
                    :percentage="project.funding_stats?.funding_percentage"
                  />
                </div>
              </div>
            </div>
            <div v-else-if="project && !project.plans?.length" 
              class="bg-gray-50 rounded-lg p-6 text-center text-sm text-gray-600"
            >
              Ce projet n'est plus disponible au financement.
            </div>
          </div>
        </div>
      </div>
    </div>

    <GiftModal
      v-if="isModalOpen"
      :is-open="isModalOpen"
      @close="closeModal"
      @submit="handleGiftSubmit"
    />

    <!-- Bulle de dialogue pour le panier -->
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="transform -translate-y-2 opacity-0"
      enter-to-class="transform translate-y-0 opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="transform translate-y-0 opacity-100"
      leave-to-class="transform -translate-y-2 opacity-0"
    >
      <div 
        v-if="showCartBubble"
        class="fixed bottom-20 right-4 lg:bottom-8 lg:right-8 max-w-xs z-50"
      >
        <div class="relative">
          <!-- Bulle de dialogue -->
          <div class="bg-white p-4 rounded-lg shadow-xl border-2 border-zebu-orange">
            <div class="flex items-start space-x-4">
              <!-- Message -->
              <div>
                <p class="text-gray-800">
                  Pour finaliser votre soutien, rendez-vous sur votre
                </p>
                <router-link
                  to="/cart"
                  class="inline-flex items-center mt-2 text-sm text-zebu-orange hover:text-zebu-orange-dark font-medium"
                >
                  panier
                  <svg class="ml-1 w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clip-rule="evenodd" />
                  </svg>
                </router-link>
              </div>
            </div>
          </div>
          <!-- Triangle de la bulle -->
          <div class="absolute -bottom-2 right-12 w-4 h-4 transform rotate-45 bg-white border-r-2 border-b-2 border-zebu-orange"></div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { memberareaService } from '@/services/memberarea.service';
import { useAuthStore } from '@/stores/auth';
import { useTemporaryCartStore } from '@/stores/temporaryCart.store';
import GiftModal from '@/components/modals/GiftModal.vue';
import ProgressBar from '@/components/ProgressBar.vue';
import { useToast } from 'vue-toastification';
import { sanitizeHtml } from '@/utils/sanitize';
import fallbackImage from '@/assets/fallback.jpg';

const route = useRoute();
const router = useRouter();
const toast = useToast();
const authStore = useAuthStore();
const temporaryCartStore = useTemporaryCartStore();
const baseUrl = import.meta.env.VITE_API_URL;
const oldBaseUrl = 'https://app.zebu.net';
const loading = ref(true);
const error = ref(null);
const project = ref(null);
const notification = ref(null);
const openPeasants = ref({});
const isGiftMode = computed(() => route.query.gift === 'true');
const isModalOpen = ref(false);
const selectedPlanId = ref(null);
const isProcessingGift = ref(false);
const addToCartError = ref(null);

// Nouvelle ref pour la bulle de dialogue du panier
const showCartBubble = ref(false);

// Fonction pour gérer les trois sources d'images possibles
const getProjectImage = (cover) => {
  if (!cover) return fallbackImage
  
  // Nettoyer le chemin de l'image (enlever le premier slash si présent)
  const cleanPath = cover.startsWith('/') ? cover.slice(1) : cover
  const filename = cleanPath.split('/').pop()
  
  // On commence toujours par l'ancien système
  return `https://app.zebu.net/uploads/projects/${filename}`
}

// Gestionnaire d'erreur pour les images avec fallback en cascade
const handleImageError = (event) => {
  const img = event.target
  const currentSrc = img.src
  const filename = currentSrc.split('/').pop()
  
  // Ordre précis de fallback
  if (currentSrc.includes('app.zebu.net')) {
    // 1. Si l'ancien système échoue, on essaie le backend local
    console.log('Ancien système échoué, tentative backend local')
    img.src = `${baseUrl}/uploads/projects/${filename}`
  } else if (currentSrc.includes(baseUrl)) {
    // 2. Si le backend local échoue, on essaie le bucket
    console.log('Backend local échoué, tentative bucket')
    img.src = `https://bucket.zebu.net/images/${filename}`
  } else if (currentSrc.includes('bucket.zebu.net')) {
    // 3. Si le bucket échoue, on utilise le fallback
    console.log('Bucket échoué, utilisation du fallback')
    img.src = fallbackImage
  }
}

// Fonction pour basculer l'état de l'accordéon
const togglePeasant = (peasantId) => {
  openPeasants.value[peasantId] = !openPeasants.value[peasantId];
};

// Fonction pour obtenir les plans d'un paysan
const getPeasantPlans = (peasant) => {
  if (!project.value?.plans) return [];
  return project.value.plans.filter(plan => 
    peasant.allIds.some(id => plan.peasantId === id)
  );
};

// Récupérer les paysans du projet
const projectPeasants = computed(() => {
  if (!project.value?.peasants) return [];
  return project.value.peasants;
});

// Formatage des données
const formatAmount = (amount) => {
  return new Intl.NumberFormat('fr-FR').format(amount);
};

const formatName = (peasant) => {
  return `${peasant.firstname} ${peasant.lastname}`;
};

// Gestion des notifications
const showNotification = (message, type = 'success') => {
  notification.value = { message, type };
  setTimeout(() => {
    notification.value = null;
  }, 3000);
};

const addToCart = async (projectId) => {
  try {
    addToCartError.value = null;

    // Si l'utilisateur n'est pas connecté
    if (!authStore.isAuthenticated) {
      // Stocker le plan dans le store temporaire
      temporaryCartStore.setPlan({
        projectId: project.value.id,
        name: project.value.name,
        amount: project.value.funding_stats.plan_price
      }, isGiftMode.value);

      // Rediriger vers l'inscription avec plan
      router.push('/register-with-plan');
      return;
    }

    // Si c'est un cadeau, ouvrir le modal
    if (isGiftMode.value) {
      selectedPlanId.value = projectId;
      isModalOpen.value = true;
      return;
    }

    loading.value = true;
    const response = await memberareaService.addToCart(projectId, false);
    
    if (response.success) {
      notification.value = {
        message: 'Projet ajouté au panier avec succès',
        type: 'success'
      };
      
      toast.success('Projet ajouté au panier avec succès', {
        timeout: 5000,
        closeButton: true
      });

      showCartNotification();
    } else {
      if (response.availablePlans !== undefined) {
        project.value.funding_stats.remaining_plans = response.availablePlans;
      }
      throw new Error(response.message);
    }
  } catch (error) {
    console.error('Error adding to cart:', error);
    let errorMessage = error.message;
    
    if (error.response?.data) {
      errorMessage = error.response.data.message;
      if (error.response.data.availablePlans !== undefined) {
        project.value.funding_stats.remaining_plans = error.response.data.availablePlans;
      }
    }
    
    addToCartError.value = errorMessage;
    toast.error(errorMessage || 'Une erreur est survenue lors de l\'ajout au panier');
  } finally {
    loading.value = false;
  }
};

// Fonction pour afficher la bulle de dialogue du panier
const showCartNotification = () => {
  showCartBubble.value = true;
  setTimeout(() => {
    showCartBubble.value = false;
  }, 10000); // La bulle disparaît après 10 secondes
};

const handleGiftSubmit = async (beneficiaryInfo) => {
  try {
    addToCartError.value = null;
    isProcessingGift.value = true;
    const response = await memberareaService.addToCart(project.value.id, true, {
      email: beneficiaryInfo.email,
      name: beneficiaryInfo.name,
      message: beneficiaryInfo.message
    });

    if (response.success) {
      toast.success('Le cadeau a été ajouté au panier');
      // Mise à jour des stats du projet avec les nouvelles informations
      if (response.data.availablePlans !== undefined) {
        project.value.funding_stats.remaining_plans = response.data.availablePlans;
      }
      isModalOpen.value = false;
      router.push('/cart');
    } else {
      // Gestion des réponses avec success: false
      if (response.availablePlans !== undefined) {
        project.value.funding_stats.remaining_plans = response.availablePlans;
      }
      throw new Error(response.message);
    }
  } catch (error) {
    console.error('Erreur lors de l\'ajout au panier:', error);
    let errorMessage = error.message;
    
    // Gestion des erreurs HTTP
    if (error.response?.data) {
      errorMessage = error.response.data.message;
      if (error.response.data.availablePlans !== undefined) {
        project.value.funding_stats.remaining_plans = error.response.data.availablePlans;
      }
    }
    
    addToCartError.value = errorMessage;
    toast.error(errorMessage || 'Une erreur est survenue lors de l\'ajout au panier');
  } finally {
    isProcessingGift.value = false;
  }
};

const closeModal = () => {
  isModalOpen.value = false;
  selectedPlanId.value = null;
};

// Chargement initial
onMounted(async () => {
  try {
    const response = await memberareaService.getProjectDetails(route.params.id);
    project.value = response.project;
  } catch (err) {
    error.value = 'Erreur lors du chargement du projet';
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
/* Animation de chargement des images */
img {
  transition: opacity 0.3s ease-in-out;
}

img[src="/placeholder-project.jpg"] {
  opacity: 0.5;
}

/* Animation pour les notifications */
.notification-enter-active,
.notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter-from,
.notification-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* Mettre à jour le style pour le fallback */
img[src$="fallback.jpg"] {
  opacity: 0.5;
  background-color: #f3f4f6;
}
</style> 